import React, { useState, useEffect, useCallback } from "react";
import produce from "immer";

import DropZone from "./DropZone";
import TrashDropZone from "./TrashDropZone";
import Row from "./Row";
import initialData from "./initial-data";
import SideBar from "./SideBar";
import {
  handleMoveWithinParent,
  handleMoveToDifferentParent,
  handleMoveSidebarComponentIntoParent,
  handleRemoveItemFromLayout,
} from "./helpers";

import { SIDEBAR_PID_ITEMS, SIDEBAR_GENERAL_ITEMS, SIDEBAR_ITEM, COMPONENT, COLUMN } from "./DropConstants";

import { withStyles } from '@mui/styles';
import UiPidsConstructorStyles from "../UiPidsConstructorStyles";
import CommonStyles from "../../../../common/CommonStyles";
import { uniqueId } from "lodash";

import { getLastRstData } from "../../../../utils";
import ArrowTooltipCss from '../../../../common/ArrowTooltipCss';
import ErrorIcon from '@mui/icons-material/ErrorOutline';

const styles = {
  ...CommonStyles,
  ...UiPidsConstructorStyles,
};

const Container = ({
  isForm,
  classes,
  callbacks,
  custom_pid,
  isDuplicate,
  form_ancestor,
  initial_layout,
  setup_rbox_light,
}) => {
  const initialLayout = initial_layout || [];
  const initialComponents = initialData.components;
  const [layout, setLayout] = useState(initialLayout);
  const [components, setComponents] = useState(initialComponents);
  const [customAncestor, setCustomAncestor] = useState(form_ancestor);

  const handleDropToTrashBin = useCallback(
    (dropZone, item) => {
      const splitItemPath = item.path.split("-");
      setLayout(handleRemoveItemFromLayout(layout, splitItemPath));
    },
    [layout]
  );

  const handleDrop = useCallback(
    (dropZone, item) => {

      const splitDropZonePath = dropZone.path.split("-");
      const pathToDropZone = splitDropZonePath.slice(0, -1).join("-");

      const newItem = { id: item.id, type: item.type };
      if (item.value) newItem.value = item.value;
      if (item.component) newItem.component = item.component;
      if (item.pids) newItem.pids = item.pids;
      if (item.type === COLUMN) {
        newItem.children = item.children;
      }

      // sidebar into
      if (item.type === SIDEBAR_ITEM) {//SIDEBAR_ITEM
        // 1. Move sidebar item into page
        const newComponent = {
          id: uniqueId(),
          ...item.component
        };
        const newItem = {
          id: newComponent.id,
          type: COMPONENT,
          ...item.component
        };
        setComponents({
          ...components,
          [newComponent.id]: newComponent
        });
        setLayout(
          handleMoveSidebarComponentIntoParent(
            layout,
            splitDropZonePath,
            newItem
          )
        );
        return;
      }

      // move down here since sidebar items dont have path
      const splitItemPath = item.path.split("-");
      const pathToItem = splitItemPath.slice(0, -1).join("-");

      // 2. Pure move (no create)
      if (splitItemPath.length === splitDropZonePath.length) {
        // 2.a. move within parent
        if (pathToItem === pathToDropZone) {
          setLayout(
            handleMoveWithinParent(layout, splitDropZonePath, splitItemPath)
          );
          return;
        }

        // 2.b. OR move different parent
        // TODO FIX columns. item includes children
        setLayout(
          handleMoveToDifferentParent(
            layout,
            splitDropZonePath,
            splitItemPath,
            newItem
          )
        );
        return;
      }

      // 3. Move + Create
      setLayout(
        handleMoveToDifferentParent(
          layout,
          splitDropZonePath,
          splitItemPath,
          newItem
        )
      );
    },
    [layout, components]
  );

  const handleRendererChange = (path, value, key, componentId) => {
    const rowIndex = path.split('-')[0];
    const columnIndex = path.split('-')[1];

    const updatedLayout = produce(layout, mutableLayout => {
      const mutableComponent = mutableLayout[rowIndex].children[columnIndex].children.find(el => el.id === componentId);

      if (mutableComponent.renderer) mutableComponent.renderer[key] = value;
      else mutableComponent.renderer = { [key]: value };

      if (value === 'no icon' || (!isForm && key.toString() === value)
        || (isForm && (key === 'true' || key === 'false') && value.toString() === '')) delete mutableComponent.renderer[key];
      if (!Object.keys(mutableComponent.renderer).length) delete mutableComponent.renderer;
    });
    setLayout(updatedLayout);
  };

  const handleHtmlChange = (path, componentId, value) => {
    const rowIndex = path.split('-')[0];
    const columnIndex = path.split('-')[1];

    const updatedLayout = produce(layout, mutableLayout => {
      const mutableComponent = mutableLayout[rowIndex].children[columnIndex].children.find(el => el.id === componentId);
      mutableComponent.value = value;
    });
    setLayout(updatedLayout);
  };

  const setCustomAncestorToFormState = (pid) => {
    if (callbacks) {
      callbacks.setCustomComponentChange('custom_component', []);
      callbacks.setCustomComponentChange('custom_pid', pid);
    }
  };

  useEffect(() => {
    if (callbacks && callbacks.setCustomComponentChange
        && JSON.stringify(initial_layout) !== JSON.stringify(layout))
      callbacks.setCustomComponentChange('custom_component', layout);
    if (custom_pid && custom_pid.length) {
      const parentRstData = getLastRstData(custom_pid, form_ancestor, setup_rbox_light);
      if (customAncestor !== parentRstData.oid)
      setCustomAncestor(parentRstData.oid);
    } else {
      if (customAncestor !== form_ancestor)
      setCustomAncestor(form_ancestor);
    }
  }, [layout, custom_pid]);

  const renderRow = (row, currentPath) => {
    return (
      <Row
        data={row}
        key={row.id}
        isForm={isForm}
        ancestor={customAncestor}
        setup_rbox_light={setup_rbox_light}
        handleRendererChange={handleRendererChange}
        handleHtmlChange={handleHtmlChange}
        handleDrop={handleDrop}
        components={components}
        path={currentPath}
      />
    );
  };

  // dont use index for key when mapping over items
  // causes this issue - https://github.com/react-dnd/react-dnd/issues/342
  return (
    <div className={classes.body}>
      <SideBar
        classes={classes}
        custom_pid={custom_pid}
        ancestor={customAncestor}
        formAncestor={form_ancestor}
        setCustomAncestorToFormState={setCustomAncestorToFormState}
        sidebarPidsItems={SIDEBAR_PID_ITEMS}
        sidebarGeneralItems={SIDEBAR_GENERAL_ITEMS}
      />
      <div className={classes.pageContainer}>
        <div className={classes.page}>
          {layout.map((row, index) => {
            const currentPath = `${index}`;

            return (
              <React.Fragment key={`${index} ${row.id}`}>
                <DropZone
                  data={{
                    path: currentPath,
                    childrenCount: layout.length
                  }}
                  onDrop={handleDrop}
                  path={currentPath}
                />
                {renderRow(row, currentPath)}
              </React.Fragment>
            );
          })}
          <DropZone
            data={{
              path: `${layout.length}`,
              childrenCount: layout.length
            }}
            onDrop={handleDrop}
            isLast
          />
        </div>

        <TrashDropZone
          data={{
            layout
          }}
          onDrop={handleDropToTrashBin}
        />

        {isDuplicate &&
          <div className={classes.ml40}>
            <ArrowTooltipCss text={'This view already contains same component'}>
              <ErrorIcon
                style={{
                  color: '#DD5900',
                  marginTop: '5px',
                }}
              />
            </ArrowTooltipCss>
          </div>
        }
      </div>
    </div>
  );
};

export default withStyles(styles)(Container);
